import React, { useState, useEffect, useCallback } from 'react';
import { X, Loader2, CalendarClock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

// Working window shown to admin: 06:00 -> 22:00 (hours 6..21 as hourly blocks)
const START_HOUR = 6;
const END_HOUR = 22;
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);

const fmt = (h) => {
  const a = `${String(h).padStart(2, '0')}:00`;
  const b = `${String(h + 1).padStart(2, '0')}:00`;
  return `${a}–${b}`;
};

const emptyWeekly = () => DAYS.reduce((acc, d) => ({ ...acc, [d.key]: [] }), {});

export const VeyonikaAvailabilityModal = ({ providerId, providerName, token, isOpen, onClose }) => {
  const [weekly, setWeekly] = useState(emptyWeekly());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!providerId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/veyonika-availability/${providerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load availability');
      setWeekly({ ...emptyWeekly(), ...(data.weekly || {}) });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [providerId, token]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const toggleSlot = (dayKey, hour) => {
    setWeekly((prev) => {
      const cur = new Set(prev[dayKey] || []);
      cur.has(hour) ? cur.delete(hour) : cur.add(hour);
      return { ...prev, [dayKey]: Array.from(cur).sort((a, b) => a - b) };
    });
  };

  const clearDay = (dayKey) => setWeekly((prev) => ({ ...prev, [dayKey]: [] }));

  const totalSlots = DAYS.reduce((sum, d) => sum + (weekly[d.key]?.length || 0), 0);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/veyonika-availability/${providerId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ weekly }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save');
      onClose(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="veyonika-availability-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[88vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <CalendarClock size={20} className="text-purple-600" />
            Veyonika Availability
          </h2>
          <button onClick={() => onClose(false)} data-testid="close-availability-modal"><X size={20} /></button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          {providerName} · pick the hourly slots this care-giver is available for Veyonika work, day by day.
        </p>

        {error && <div className="mb-3 p-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>}

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-purple-600" /></div>
        ) : (
          <div className="space-y-4">
            {DAYS.map((d) => (
              <div key={d.key} className="border rounded-lg p-3" data-testid={`avail-day-${d.key}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-800">{d.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{weekly[d.key]?.length || 0} slot(s)</span>
                    {weekly[d.key]?.length > 0 && (
                      <button onClick={() => clearDay(d.key)} className="text-xs text-red-500 hover:underline">Clear</button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {HOURS.map((h) => {
                    const active = (weekly[d.key] || []).includes(h);
                    return (
                      <button
                        key={h}
                        onClick={() => toggleSlot(d.key, h)}
                        data-testid={`avail-slot-${d.key}-${h}`}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                          active
                            ? 'bg-purple-600 text-white border-purple-600'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-purple-400'
                        }`}
                      >
                        {fmt(h)}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between mt-5 pt-4 border-t">
          <span className="text-sm text-gray-500" data-testid="avail-total-slots">Total: {totalSlots} slot(s)/week</span>
          <div className="flex gap-2">
            <button onClick={() => onClose(false)} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              data-testid="save-availability-btn"
              className="px-4 py-2 text-sm rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Loader2 size={16} className="animate-spin" />}
              Save Availability
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VeyonikaAvailabilityModal;
